import type {
  AttendeeAvatarDto,
  GambitAttendanceStatus,
  GambitProposalSummaryDto,
  LocationGambitDto,
} from '@/core/data/gambit/gambit.interface';
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { LocationChessFeature, LocationGeneralFeature, LocationType } from '@/core/data/location/location.type';
import { MapLocationSource, MapLocationType } from '@/core/data/location/location.type';
import type { RatingDisplayMode } from '@/core/data/user/user.type';
// import type { GambitLocation } from '@/views/components/gam-gambits/GamGambitResultCard.vue';
import type { SparseClubDto } from '../club/club.interface';
import type { GambitCategory, GambitChessSetPolicy, GambitRepeat, GambitType } from '../gambit/gambit.type';

export interface MapClusterData {
  map?: google.maps.Map;
  items?: MapLocationDto[] | null;
  onClickMarker?: (item: MapLocationDto) => void;
  onClickChessboard?: (item: MapLocationDto) => void;
  activeSpotId?: string;
  dotMode?: boolean;
}

export interface CityDto {
  id?: string;
  name: string;
  address: string;
  coordinates: GeoCoords;
  viewportHigh?: GeoCoords;
  viewportLow?: GeoCoords;
  type: LocationType;
  timezone?: string;
  googlePlaceId?: string;
  sparkableLocationIds?: string[];
  hasRevealSpots?: boolean;
}

export interface BasicLocationInfo {
  id: string;
  coordinates: GeoCoords;
  type: LocationType;
  chessFeatures: LocationChessFeature[];
  generalFeatures: LocationGeneralFeature[];
  private: boolean;
  favorite: boolean;
  isVetted: boolean;
  isSparked?: boolean;
  name: string;
  address: string;
  timezone?: string;
}

interface BaseLocationUserDto {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  profileImageBlobHash: string | null;
}

export interface BaseLocationDto extends BasicLocationInfo {
  // image?: ImageDto | null;
  gambit?: LocationGambitDto | null;
  visitors?: BaseLocationUserDto[];
  distance?: number;
  nextGambits?: NextGambitDto[];
  chessSetCount?: number;
  sparkAvailabilityCount?: number;
  sparkIntentCount?: number;
  activeChessboardCount?: number;
  hasPlayableBoard?: boolean;
  coverBlobHash?: string | null;
  coverUpdatedAt?: string | null;
  mapBlobHash?: string | null;
}

export interface MapLocationDto {
  mapLocationType: MapLocationType;
  id: string;
  coordinates: GeoCoords;
  type?: LocationType;
  chessFeatures?: LocationChessFeature[];
  generalFeatures?: LocationGeneralFeature[];
  private?: boolean;
  favorite?: boolean;
  name?: string;
  address?: string;
  nextGambits?: LocationGambitDto[] | null;
  visitorCount?: number;
  activeChessboardCount?: number;
  firstActiveChessboardId?: string | null;
  isVetted?: boolean;
  isSparked?: boolean;
  hasPlayableBoard?: boolean;
  coverBlobHash?: string | null;
}

export interface VisitorDto {
  id: string;
  username: string;
  rating: number;
  profileImageBlobHash: string | null;
  ratingDisplayMode?: RatingDisplayMode;
  isCheckedIn: boolean;
  checkOutTime: string | null;
  chessSetCount?: number;
}

export interface NextGambitAttendance {
  id: string;
  // "uuid": "7c0776f3-dab4-4827-91c5-f00d5fe6cf9a",
  createdAt: string;
  updatedAt: string;
  gambitId: string;
  isOrganizer: boolean;
  userId: string;
  chessSetCount: number;
  status: GambitAttendanceStatus;
  emailMessageId: null | string;
}

export interface NextGambitDto {
  id: string;
  // "uuid": string,
  createdAt: string;
  updatedAt: string;
  start: string;
  end: string;
  location?: BaseLocationDto;
  locationPoint: {
    lat: number;
    lng: number;
  };
  distance: number;
  locationId: string;
  cityId: string | null;
  locationImageBlobHash: string | null;
  ownerId: string | null;
  clubId: string | null;
  chessSetPolicy: GambitChessSetPolicy;
  gambitType: GambitType[];
  /* Null on the rows the API synthesises for an unconfirmed proposal. */
  gambitCategory: GambitCategory | null;
  // sparkId: string | null;
  streamed: boolean;
  timed: boolean;
  isChallenge: boolean;
  isConfirmed: boolean;
  title: string;
  description: string | null;
  private: boolean;
  deletedAt: string | null;
  attendances: NextGambitAttendance[];
  attendeeCount: number;
  minEloRating: number;
  maxEloRating: number;
  participantIds: string[];
  parentId: string | null;
  repeat: GambitRepeat;
  repeatUntil: string | null;
  maxAttendees: number | null;
  minConfirmations: number | null;
  isAttending: boolean;
  canEdit: boolean;
  proposal?: GambitProposalSummaryDto | null;
  attendeeAvatars?: AttendeeAvatarDto[];
}

export interface DetailLocationDto extends BasicLocationInfo {
  about: string | null;
  chessSet: boolean;
  createdAt: string;
  updatedAt: string;
  chessSetCount?: number | null;
  chessClockCount?: number | null;
  chessTableCount?: number | null;
  giantChessBoardCount?: number | null;
  giantChessPieces?: boolean | null;
  user?: DetailLocationUserDto | null;
  // image: ImageDto | null;
  hostedClubs: SparseClubDto[];
  visitors: VisitorDto[];
  nextGambits?: NextGambitDto[];
  handshakes: HandshakeDto[];
  pendingHandshakes: HandshakeDto[];
  isCheckedIn: boolean;
  recenCheckIns: string[];
  openingHours: Array<{ day: number; start: string | null; end: string | null }> | null;
  coverBlobHash?: string | null;
  coverUpdatedAt?: string | null;
}

export interface AdminDetailLocationDto extends DetailLocationDto {
  verified: boolean;
  // image: ImageDto | null;
}

export interface DetailLocationUserDto {
  id: string;
  username: string;
}

interface ShallowLocationAttendance {
  id: 'dx5u9t4gdcik';
  uuid: '3659f127-ba48-47a7-a124-c3b67cef7edd';
  createdAt: '2026-02-02T11:06:29.361Z';
  updatedAt: '2026-02-03T05:17:53.608Z';
  gambitId: 'shf41epts6jo';
  isOrganizer: false;
  userId: 'q99o17lpom5v';
  chessSetCount: 0;
  status: 'confirmed';
  emailMessageId: null;
}

interface ShallowLocationGambit {
  id: string;
  // uuid: string;
  createdAt: string;
  updatedAt: string;
  start: string;
  end: string;
  locationPoint: {
    lat: number;
    lng: number;
  };
  distance: number;
  locationId: string;
  cityId: string | null;
  locationImageBlobHash: string;
  ownerId: string | null;
  clubId: string | null;
  chessSetPolicy: GambitChessSetPolicy;
  gambitType: GambitType[];
  gambitCategory: GambitCategory;
  // sparkId: null;
  streamed: boolean;
  timed: boolean;
  isChallenge: boolean;
  isConfirmed: boolean;
  title: string;
  description: string;
  private: boolean;
  attendances: ShallowLocationAttendance[];
  attendeeCount: number;
  minEloRating: number | null;
  maxEloRating: number | null;
  participantIds: string[];
  parentId: string | null;
  repeat: GambitRepeat;
  repeatUntil: string | null;
  maxAttendees: null;
  minConfirmations: null;
  isAttending: false;
  proposal?: GambitProposalSummaryDto | null;
}

interface ShallowLocationClub {
  id: string;
  name: string;
  username: string;
  profileImageBlobHash: string | null;
}

interface ShallowLocationVisitor {
  id: string;
  username: string;
  profileImageBlobHash: string | null;
  isCheckedIn: boolean;
  firstName: string;
  lastName: string;
  start: string;
  end: string | null;
}

export interface ShallowLocationDto {
  id: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  type: LocationType;
  chessFeatures: LocationChessFeature[];
  generalFeatures: LocationGeneralFeature[];
  chessSet: boolean;
  image?: string;
  private: boolean;
  favorite: boolean;
  name: string;
  address: string;
  about: string | null;
  user: any | null;
  chessSetCount: number;
  chessClockCount: number;
  giantChessBoardCount: number;
  giantChessPieces: boolean;
  chessTableCount: number | null;
  createdAt: string;
  updatedAt: string;
  nextGambits: ShallowLocationGambit[];
  hostedClubs: ShallowLocationClub[];
  visitors: ShallowLocationVisitor[];
  handshakes: HandshakeDto[];
  pendingHandshakes: HandshakeDto[];
  isCheckedIn: boolean;
  timezone: string;
  isVetted: true;
  isSparked: boolean;
  openingHours: Array<{ day: number; start: string | null; end: string | null }> | null;
  coverBlobHash: string | null;
  coverUpdatedAt: string | null;
}

export interface SearchLocationDto {
  id?: string;
  accuracy?: string;
  name?: string;
  label?: string;
  subLabel?: string;
  address?: string;
  type?: LocationType;
  googlePlaceId?: string;
  coordinates: GeoCoords;
  nearbyPlaceAddress?: string;
  source?: MapLocationSource;
  cityName?: string;
}

export interface GooglePlaceResultDto {
  id: string;
  label: string;
  subLabel: string;
  name: string;
  address: string;
  coordinates: GeoCoords;
  viewportLow?: GeoCoords;
  viewportHigh?: GeoCoords;
  googleMapsResponse?: string;
  source: MapLocationSource;
  nearbyPlaceAddress?: string;
  timezone?: string;
}

export type SearchLocationOptions = {
  geo?: GeoCoords;
  internal?: boolean;
  mapbox?: boolean;
  onlyCities?: boolean;
  sessionToken?: string;
};

export enum GeoDataSource {
  CLOUDFLARE_IP = 'cloudflare_ip',
  CLOUDFLARE_COUNTRY = 'cloudflare_country',
  BROWSER_API = 'browser_api',
  UNKNOWN = 'unknown',
  FALLBACK = 'fallback',
}
export interface UserGeoData {
  accuracy: number;
  location: GeoCoords;
  source: GeoDataSource;
  updatedAt: string;
}

export interface UserLocationData extends GeoCoordinates {
  city: string;
  country: string;
}

export interface UserGeoResponse extends UserGeoData {
  response?: UserLocationData;
}

export interface GeoCoords {
  lat: number;
  lng: number;
}

export interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

export interface ImageDto {
  id: string;
  imageBlobHash: string;
  liked: boolean;
  numberOfLikes?: number;
  user: AuthorDto | null;
  createdAt?: string;
  /** Zone the date is shown in. A handshake photo uses the handshake's zone, so it matches the list. */
  timezone?: string;
  handshakeId?: string;
  /** Chess960 number this photo belongs to. Absent on a meeting photo. */
  positionIndex?: number | null;
  /** Either player in the handshake may put this photo on a position. */
  canAssignPosition?: boolean;
  /** How the collection colours this photo's position. Gray covers no position and an unknown player. */
  positionTone?: 'unique' | 'reported' | 'repeat' | 'unknown';
}

export interface AuthorDto {
  id: string;
  username: string;
  emoji?: string;
  profileImageBlobHash: string | null;
}

export interface CommentDto {
  id: string;
  rating: number;
  message?: string;
  imageBlobHash?: string;
  user?: AuthorDto;
  createdAt?: string;
}

export interface GamMapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface MapMinMaxRadius {
  min: number;
  max: number;
}

export interface GisRadius {
  default: number;
  max: number;
}

export interface MapZoom {
  zoom: number;
  minZoom: number;
}

export interface GamPinObject {
  element: HTMLDivElement;
  coordinates: GeoCoords;
}

export interface AddLocationFormData {
  name: string;
  address: string;
  about?: string;
  private?: boolean;
  coordinates?: GeoCoords;
  images: (File | null | string | undefined)[];
  type: LocationType;
  chessFeatures: LocationChessFeature[];
  generalFeatures: LocationGeneralFeature[];
  chessSetCount?: number | null;
  chessClockCount?: number | null;
  chessTableCount?: number | null;
  giantChessBoardCount?: number | null;
  giantChessPieces?: boolean;
  openingHours?: Array<{ day: number; start: string | null; end: string | null }> | null;
}

export interface AddLocationCommentData {
  rating: number;
  message?: string;
  image?: File | null | string;
}

export interface GooglePlaceQuery {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
  sessionToken: string;
}
